import { useEffect, useState, useRef, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FAMILIES } from "@/lib/format";
import { UserPlus, Trash2, Image as ImageIcon, Check, Loader2, Upload, Users, LogOut } from "lucide-react";
import { toast } from "sonner";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Settings() {
  const { user, logoutAll } = useAuth();
  const isOwner = user?.role === "owner";
  const [tab, setTab] = useState(isOwner ? "staff" : "photos");

  const doLogoutAll = async () => {
    if (!window.confirm("Sign out of every device, including this one?")) return;
    await logoutAll();
  };

  return (
    <div className="mf-fade-in">
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight">Settings</h1>
      <p className="mb-4 text-sm text-[#98989d]">Signed in as {user?.email}</p>

      <div className="mb-4 flex items-center gap-2">
        {isOwner && (
          <button data-testid="tab-staff" onClick={() => setTab("staff")} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${tab === "staff" ? "bg-[#2997ff] text-white" : "border border-white/10 text-[#98989d]"}`}>
            <Users size={15} /> Staff
          </button>
        )}
        <button data-testid="tab-photos" onClick={() => setTab("photos")} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${tab === "photos" ? "bg-[#2997ff] text-white" : "border border-white/10 text-[#98989d]"}`}>
          <ImageIcon size={15} /> Model photos
        </button>
        <button data-testid="logout-all" onClick={doLogoutAll} className="ml-auto flex items-center gap-2 rounded-full border border-[#ff453a]/30 px-4 py-2 text-sm font-semibold text-[#ff453a]">
          <LogOut size={15} /> Log out all devices
        </button>
      </div>

      {tab === "staff" && isOwner ? <StaffPanel /> : <ModelPhotosPanel />}
    </div>
  );
}

function StaffPanel() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/auth/staff"); setStaff(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
    setBusy(true);
    try { await api.post("/auth/staff", form); setForm({ name: "", email: "", password: "" }); toast.success("Staff added"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this staff member?")) return;
    try { await api.delete(`/auth/staff/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const input = "h-11 w-full rounded-xl border border-white/10 bg-black px-3 text-sm outline-none focus:border-[#2997ff]";

  return (
    <div>
      <div className="mb-4 space-y-3 rounded-2xl border border-white/10 bg-[#121214] p-4">
        <div className="text-sm font-semibold">Add staff</div>
        <input data-testid="staff-name-input" className={input} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="staff-email-input" className={input} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="staff-password-input" className={input} type="text" placeholder="Temporary password (min 10 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <button data-testid="add-staff-button" onClick={add} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2997ff] py-3 font-semibold active:scale-[0.98] disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <UserPlus size={16} />} Add staff
        </button>
      </div>

      <div className="space-y-2">
        {staff.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#121214] p-4">
            <div>
              <div className="font-semibold">{s.name} {s.role === "owner" && <span className="ml-1 rounded-full bg-[#2997ff]/15 px-2 py-0.5 text-[10px] text-[#2997ff]">OWNER</span>}</div>
              <div className="text-xs text-[#98989d]">{s.email}</div>
            </div>
            {s.role !== "owner" && (
              <button data-testid={`remove-staff-${s.id}`} onClick={() => remove(s.id)} className="text-[#ff453a]"><Trash2 size={16} /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ModelPhotosPanel() {
  const [groups, setGroups] = useState([]);
  const [custom, setCustom] = useState({ family: "MacBook Pro", size: "", chip: "" });
  const fileRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/model-photos/groups"); setGroups(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pick = (g) => { setTarget(g); fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !target) return;
    setUploading(true);
    try {
      const image = await fileToDataUrl(file);
      await api.post("/model-photos", { family: target.family, size: target.size, chip: target.chip, image });
      toast.success(`Photo set for ${target.group_label}`);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); setTarget(null); }
  };

  const removePhoto = async (gk) => {
    try { await api.delete(`/model-photos/${encodeURIComponent(gk)}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const input = "h-10 w-full rounded-xl border border-white/10 bg-black px-3 text-sm outline-none focus:border-[#2997ff]";

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif" className="hidden" onChange={onFile} data-testid="model-photo-input" />
      <p className="mb-3 text-sm text-[#98989d]">Upload one studio PNG per model group. Every unit in that group uses it on its WhatsApp card automatically.</p>

      <div className="mb-4 space-y-2 rounded-2xl border border-white/10 bg-[#121214] p-4">
        <div className="text-sm font-semibold">Add a custom group</div>
        <div className="grid grid-cols-3 gap-2">
          <select className={input} value={custom.family} onChange={(e) => setCustom({ ...custom, family: e.target.value })}>
            {FAMILIES.map((f) => <option key={f}>{f}</option>)}
          </select>
          <input className={input} placeholder='Size 14"' value={custom.size} onChange={(e) => setCustom({ ...custom, size: e.target.value })} />
          <input className={input} placeholder="Chip M3 Pro" value={custom.chip} onChange={(e) => setCustom({ ...custom, chip: e.target.value })} />
        </div>
        <button
          data-testid="upload-custom-group-button"
          onClick={() => { if (!custom.size || !custom.chip) { toast.error("Enter size and chip"); return; } setTarget({ ...custom, group_label: `${custom.family} ${custom.size} ${custom.chip}` }); fileRef.current?.click(); }}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-[#161618] py-2.5 text-sm font-semibold active:scale-[0.98]"
        >
          <Upload size={15} /> Choose photo for custom group
        </button>
      </div>

      {uploading && <div className="mb-3 flex items-center gap-2 text-sm text-[#2997ff]"><Loader2 className="animate-spin" size={15} /> Uploading…</div>}

      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g.group_key} data-testid={`photo-group-${g.group_key}`} className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#121214] p-4">
            <div>
              <div className="font-semibold">{g.group_label}</div>
              <div className="text-xs text-[#98989d]">{g.count} unit{g.count > 1 ? "s" : ""} · {g.has_photo ? <span className="text-[#30d158]">photo set</span> : <span className="text-[#ff9f0a]">silhouette</span>}</div>
            </div>
            <div className="flex items-center gap-2">
              {g.has_photo && <Check size={16} className="text-[#30d158]" />}
              <button data-testid={`set-photo-${g.group_key}`} onClick={() => pick(g)} className="rounded-full border border-white/10 bg-[#161618] px-3 py-1.5 text-xs font-semibold active:scale-95">{g.has_photo ? "Replace" : "Upload"}</button>
              {g.has_photo && <button onClick={() => removePhoto(g.group_key)} className="text-[#ff453a]"><Trash2 size={15} /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
